/**
 * pmsRuntimeStore — estado runtime compartido de la demo PMS.
 *
 * Un único módulo con cuatro colecciones (reservations + issues +
 * availability blocks + owner stay requests) y sus respectivos subscribers.
 * Cualquier componente que consuma alguna de las hooks
 * (`usePmsReservations`, `usePmsIssues`, `usePmsBlocks`, `usePmsOwnerStays`)
 * se re-renderiza cuando cambia SU slice, sin acoplarse al resto.
 *
 * Inicialización:
 *   - Reservations seed  ← RESERVATIONS        (marcadas `origin: "seed"`)
 *   - Issues seed        ← PROPERTY_ISSUES     (marcadas `origin: "seed"`)
 *   - Blocks seed        ← AVAILABILITY_BLOCKS (marcadas `origin: "seed"`)
 *   - Owner stays seed   ← OWNER_STAY_REQUESTS (marcadas `origin: "seed"`)
 *
 * Mutaciones soportadas:
 *   - createReservation(payload)         → alta manual (WhatsApp/Manual/Direct)
 *   - createIssue(payload)               → puede generar block linked automáticamente
 *   - updateIssue(id, patch)             → resolver un issue cierra su block linked
 *   - addIssueNote(id, text)
 *   - createBlock(payload)               → uso directo si algún flujo lo requiere
 *   - updateBlock(id, patch)
 *   - createOwnerStayRequest(payload)    → alta desde InvestorPmsPreview
 *   - approveOwnerStayRequest(id, actor) → status→approved + OWNER_STAY block
 *   - rejectOwnerStayRequest(id, reason, actor) → status→rejected (sin block)
 *
 * No hay Redux, no hay Context, no hay persistencia. Cuando existan
 * endpoints reales, se sustituye la capa sin cambiar consumidores.
 */
import { useSyncExternalStore } from "react";
import {
  RESERVATIONS, PROPERTY_ISSUES, AVAILABILITY_BLOCKS, OWNER_STAY_REQUESTS,
  DEMO_TODAY_ISO,
} from "@/data/pmsDemoData";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let reservations = RESERVATIONS.map((r) => ({ ...r, origin: "seed" }));

let issues = PROPERTY_ISSUES.map((i) => ({
  ...i,
  origin: "seed",
  extra_notes: [],
}));

let blocks = AVAILABILITY_BLOCKS.map((b) => ({
  ...b,
  origin: "seed",
  resolved_at: null,
}));

let ownerStayRequests = OWNER_STAY_REQUESTS.map((r) => ({
  ...r,
  origin: "seed",
}));

const reservationListeners = new Set();
const issueListeners = new Set();
const blockListeners = new Set();
const ownerStayListeners = new Set();

function notifyReservations() { for (const fn of reservationListeners) fn(); }
function notifyIssues() { for (const fn of issueListeners) fn(); }
function notifyBlocks() { for (const fn of blockListeners) fn(); }
function notifyOwnerStays() { for (const fn of ownerStayListeners) fn(); }

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export function getReservations() { return reservations; }
export function getIssues() { return issues; }
export function getBlocks() { return blocks; }
export function getOwnerStayRequests() { return ownerStayRequests; }

function subscribeReservations(fn) { reservationListeners.add(fn); return () => reservationListeners.delete(fn); }
function subscribeIssues(fn) { issueListeners.add(fn); return () => issueListeners.delete(fn); }
function subscribeBlocks(fn) { blockListeners.add(fn); return () => blockListeners.delete(fn); }
function subscribeOwnerStays(fn) { ownerStayListeners.add(fn); return () => ownerStayListeners.delete(fn); }

/** Hooks reactivos — useSyncExternalStore garantiza consistencia sin tearing. */
export function usePmsReservations() {
  return useSyncExternalStore(subscribeReservations, getReservations);
}
export function usePmsIssues() {
  return useSyncExternalStore(subscribeIssues, getIssues);
}
export function usePmsBlocks() {
  return useSyncExternalStore(subscribeBlocks, getBlocks);
}
export function usePmsOwnerStays() {
  return useSyncExternalStore(subscribeOwnerStays, getOwnerStayRequests);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Cronología coherente con el demo: fecha DEMO_TODAY + hora real actual, así
 * los nuevos issues/blocks conviven con los seed en 2028-08-XX y no saltan a
 * la fecha real del wallclock.
 */
export function demoNowISO() {
  const time = new Date().toISOString().slice(11, 19);
  return `${DEMO_TODAY_ISO}T${time}Z`;
}

let sessionIssueCounter = 0;
function nextIssueId() {
  sessionIssueCounter += 1;
  return `I-SESSION-${String(sessionIssueCounter).padStart(3, "0")}`;
}

let sessionBlockCounter = 0;
function nextBlockId() {
  sessionBlockCounter += 1;
  return `B-SESSION-${String(sessionBlockCounter).padStart(3, "0")}`;
}

let sessionReservationCounter = 0;
function nextReservationId() {
  sessionReservationCounter += 1;
  return `R-SESSION-${String(sessionReservationCounter).padStart(3, "0")}`;
}

let sessionOwnerStayCounter = 0;
function nextOwnerStayId() {
  sessionOwnerStayCounter += 1;
  return `OS-SESSION-${String(sessionOwnerStayCounter).padStart(3, "0")}`;
}

/**
 * Reporter demo. El shape ya admite futuros roles (housekeeping / reception /
 * manager / maintenance) sin cambio de auth.
 */
export const DEMO_REPORTER = {
  user_id: "user_admin_001",
  name: "Dolce Admin",
  role: "admin",
};

function autoTitle(payload) {
  const first = String(payload.description || "").trim().split("\n")[0];
  if (first) return first.length > 80 ? first.slice(0, 77) + "…" : first;
  return "Incidencia";
}

function fairnessRule(category, blocksInventory, override) {
  if (override != null) return Boolean(override);
  return category === "maintenance" && Boolean(blocksInventory);
}

// ---------------------------------------------------------------------------
// Issues — writes
// ---------------------------------------------------------------------------

/**
 * createIssue — crea una incidencia y, cuando `blocks_inventory === true`,
 * también un `AVAILABILITY_BLOCK` de tipo MAINTENANCE vinculado.
 *
 * Payload esperado:
 *   villa_id, category, description, priority, blocks_inventory,
 *   reservation_id?, photos_placeholder?, reporter?,
 *   block_start_date?, block_end_date?
 *
 * Devuelve `{ issue, block? }` para que el caller pueda referenciar ambos.
 */
export function createIssue(payload) {
  const category = payload.category || "other";
  const blocksInventory = Boolean(payload.blocks_inventory);
  const fairnessCompensable = fairnessRule(category, blocksInventory, payload.fairness_compensable);
  const issueId = payload.issue_id || nextIssueId();
  const now = demoNowISO();

  // 1) Crear el block primero (para poder linkear su id al issue).
  let block = null;
  if (blocksInventory && payload.block_start_date && payload.block_end_date) {
    block = _createBlock({
      villa_id: payload.villa_id,
      type: "MAINTENANCE",
      start_date: payload.block_start_date,
      end_date: payload.block_end_date,
      fairness_compensable: fairnessCompensable,
      reason: payload.description || autoTitle(payload),
      issue_id: issueId,
      created_by: (payload.reporter || DEMO_REPORTER).user_id,
    });
  }

  const issue = {
    issue_id: issueId,
    villa_id: payload.villa_id,
    category,
    priority: payload.priority || "medium",
    status: "open",
    blocks_inventory: blocksInventory,
    fairness_compensable: fairnessCompensable,
    reservation_id: payload.reservation_id || null,
    linked_block_id: block ? block.block_id : null,
    reporter: payload.reporter || DEMO_REPORTER,
    assignee: null,
    title: payload.title || autoTitle(payload),
    description: payload.description || "",
    photos_placeholder: Number(payload.photos_placeholder || 0),
    reported_at: now,
    resolved_at: null,
    origin: "created_in_session",
    extra_notes: [],
  };
  issues = [issue, ...issues];
  notifyIssues();
  return { issue, block };
}

/**
 * updateIssue — patch superficial + reglas derivadas:
 *   - `status === "resolved"` sella `resolved_at` si viene vacío.
 *   - Si el issue tiene `linked_block_id`, propaga `resolved_at` al block.
 */
export function updateIssue(issueId, patch) {
  let updatedIssue = null;
  issues = issues.map((i) => {
    if (i.issue_id !== issueId) return i;
    const next = { ...i, ...patch };
    if (patch.status === "resolved" && !next.resolved_at) {
      next.resolved_at = demoNowISO();
    }
    updatedIssue = next;
    return next;
  });
  if (updatedIssue) {
    // Propagar resolución al block vinculado (mantiene historial + cierra
    // bloqueo activo desde la resolución).
    if (patch.status === "resolved" && updatedIssue.linked_block_id) {
      _updateBlock(updatedIssue.linked_block_id, {
        resolved_at: updatedIssue.resolved_at,
        status: "resolved",
      });
    }
    notifyIssues();
  }
}

export function addIssueNote(issueId, text) {
  let touched = false;
  issues = issues.map((i) => {
    if (i.issue_id !== issueId) return i;
    touched = true;
    const notes = Array.isArray(i.extra_notes) ? [...i.extra_notes] : [];
    notes.push({ text: text || "Nota demo", at: demoNowISO() });
    return { ...i, extra_notes: notes };
  });
  if (touched) notifyIssues();
}

// ---------------------------------------------------------------------------
// Blocks — writes (internal; exposed via public API when useful)
// ---------------------------------------------------------------------------

function _createBlock(payload) {
  const nights = daysBetweenInclusive(payload.start_date, payload.end_date);
  const block = {
    block_id: payload.block_id || nextBlockId(),
    villa_id: payload.villa_id,
    type: payload.type || "MAINTENANCE",
    start_date: payload.start_date,
    end_date: payload.end_date,
    nights,
    fairness_compensable: Boolean(payload.fairness_compensable),
    reason: payload.reason || "",
    created_by: payload.created_by || DEMO_REPORTER.user_id,
    created_at: demoNowISO(),
    status: "active",
    origin: "created_in_session",
    issue_id: payload.issue_id || null,
    resolved_at: null,
  };
  blocks = [...blocks, block];
  notifyBlocks();
  return block;
}

function _updateBlock(blockId, patch) {
  let touched = false;
  blocks = blocks.map((b) => {
    if (b.block_id !== blockId) return b;
    touched = true;
    return { ...b, ...patch };
  });
  if (touched) notifyBlocks();
}

/** Public export (por si un futuro flujo necesita crear blocks sueltos sin issue). */
export function createBlock(payload) { return _createBlock(payload); }
export function updateBlock(blockId, patch) { _updateBlock(blockId, patch); }

// ---------------------------------------------------------------------------
// Pure date helpers (no imports desde selectors para evitar ciclos)
// ---------------------------------------------------------------------------

function daysBetweenInclusive(startISO, endISO) {
  const a = new Date(startISO + "T00:00:00Z").getTime();
  const b = new Date(endISO   + "T00:00:00Z").getTime();
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

function stayNights(checkInISO, checkOutISO) {
  const a = new Date(checkInISO  + "T00:00:00Z").getTime();
  const b = new Date(checkOutISO + "T00:00:00Z").getTime();
  return Math.max(0, Math.round((b - a) / 86400000));
}

// ---------------------------------------------------------------------------
// Reservations — writes
// ---------------------------------------------------------------------------

/**
 * createReservation — alta manual de una reserva demo. Shape 100 %
 * compatible con las reservas seed (mismos campos, más `origin` y
 * `sofa_bed`). El pricing (accommodation_revenue_eur) llega ya calculado —
 * demo se limita a persistirlo sin generar tarifas.
 *
 * Modelo pago ortogonal (importante — "unpaid" NO es un método de pago):
 *   - payment_method  : "card" | "bank_transfer" | "cash" | null
 *   - payment_status  : "paid" | "unpaid" | …
 *   - booking_status  : "confirmed" | "pending" | …
 * Los tres campos vienen ya resueltos desde el caller (el drawer manual).
 * DOKU sigue aplicando sólo cuando `payment_method === "card"`.
 *
 * Payload esperado:
 *   channel, pool_id, assigned_villa_id, check_in, check_out,
 *   accommodation_revenue_eur, currency?, guest {name, phone?, email?,
 *   adults, children, country?, sofa_bed?}, payment_method,
 *   payment_status?, booking_status?, allocation {…}.
 *
 * stay_status siempre "pre_arrival" (reserva recién creada) salvo override.
 */
export function createReservation(payload) {
  const id = payload.reservation_id || nextReservationId();
  const nights = stayNights(payload.check_in, payload.check_out);
  const reservation = {
    reservation_id: id,
    channel: payload.channel,
    pool_id: payload.pool_id,
    assigned_villa_id: payload.assigned_villa_id,
    booking_status: payload.booking_status || "confirmed",
    stay_status: payload.stay_status || "pre_arrival",
    payment_status: payload.payment_status || "unpaid",
    payment_method: payload.payment_method || null,
    check_in: payload.check_in,
    check_out: payload.check_out,
    nights,
    guest: {
      name:     payload.guest?.name || "—",
      phone:    payload.guest?.phone || null,
      email:    payload.guest?.email || null,
      adults:   Number(payload.guest?.adults || 0),
      children: Number(payload.guest?.children || 0),
      country:  payload.guest?.country || null,
      sofa_bed: Boolean(payload.guest?.sofa_bed),
    },
    accommodation_revenue_eur: Number(payload.accommodation_revenue_eur || 0),
    currency: payload.currency || "EUR",
    allocation: payload.allocation || null,
    created_at: demoNowISO(),
    origin: "created_in_session",
  };
  reservations = [...reservations, reservation];
  notifyReservations();
  return reservation;
}

/**
 * updateReservation — patch superficial sobre una reserva de la demo.
 *
 * Los tres ejes de estado (booking / stay / payment) son INDEPENDIENTES: esta
 * función no deduce ninguno a partir de otro. Cambiar el estado de estancia no
 * cobra la reserva, y confirmar no la marca como pagada.
 *
 * `assigned_villa_id: null` es un estado legítimo: la reserva existe sobre un
 * tipo de alojamiento y todavía no tiene unidad física (PRODUCT_V2_SPEC §6.1).
 */
export function updateReservation(reservationId, patch) {
  let updated = null;
  reservations = reservations.map((r) => {
    if (r.reservation_id !== reservationId) return r;
    updated = { ...r, ...patch };
    return updated;
  });
  if (updated) notifyReservations();
  return updated;
}

/**
 * cancelReservation — cancelar NO es lo mismo que salir. Solo toca el eje de
 * reserva y deja constancia de quién, cuándo y por qué; la estancia y el pago
 * conservan su estado para que el historial siga siendo legible y la reserva
 * siga apareciendo en búsquedas e informes.
 */
export function cancelReservation(reservationId, payload = {}) {
  const current = reservations.find((r) => r.reservation_id === reservationId);
  if (!current || current.booking_status === "cancelled") return current || null;
  return updateReservation(reservationId, {
    booking_status: "cancelled",
    cancellation: {
      cancelled_at: payload.cancelled_at || demoNowISO(),
      cancelled_by: payload.cancelled_by || "staff",
      reason: payload.reason || null,
      retained_revenue_eur: Number(payload.retained_revenue_eur || 0),
      refunded_eur: Number(payload.refunded_eur || 0),
    },
  });
}

/**
 * Fecha efectiva final del bloqueo. Si el issue asociado se resolvió, el
 * bloqueo deja de aplicar a fechas posteriores a la resolución (pero el
 * historial mantiene la `end_date` original).
 */
export function effectiveBlockEnd(block) {
  if (!block) return null;
  if (!block.resolved_at) return block.end_date;
  const resolvedDate = String(block.resolved_at).slice(0, 10);
  return resolvedDate < block.end_date ? resolvedDate : block.end_date;
}

// ---------------------------------------------------------------------------
// Owner stay requests — writes
// ---------------------------------------------------------------------------

/**
 * Alta demo de una solicitud de owner stay desde el InvestorPmsPreview.
 * Shape compatible con OWNER_STAY_REQUESTS seed. Status inicial "requested"
 * — todavía NO consume las 60 noches del cupo anual (eso pasa al aprobar).
 */
export function createOwnerStayRequest(payload) {
  const nights = Math.max(0, stayNights(payload.start_date, payload.end_date));
  const request = {
    request_id: payload.request_id || nextOwnerStayId(),
    villa_id: payload.villa_id,
    investor_user_id: payload.investor_user_id || null,
    investor_name: payload.investor_name || "Investor",
    start_date: payload.start_date,
    end_date: payload.end_date,
    nights,
    status: "requested",
    requested_at: payload.requested_at || demoNowISO(),
    approved_at: null,
    approved_by: null,
    rejected_at: null,
    rejected_by: null,
    rejection_reason: null,
    note: payload.note || null,
    origin: "created_in_session",
  };
  ownerStayRequests = [request, ...ownerStayRequests];
  notifyOwnerStays();
  return request;
}

/**
 * Aprobar una solicitud. Idempotente: si ya está aprobada devuelve la
 * misma request sin crear un segundo block.
 * Al aprobar crea un OWNER_STAY availability block (fairness_compensable=false).
 */
export function approveOwnerStayRequest(requestId, actor = DEMO_REPORTER) {
  const cur = ownerStayRequests.find((r) => r.request_id === requestId);
  if (!cur) return null;
  if (cur.status === "approved" || cur.status === "completed") return cur;

  const now = demoNowISO();
  const updated = {
    ...cur,
    status: "approved",
    approved_at: now,
    approved_by: actor.user_id || DEMO_REPORTER.user_id,
    rejected_at: null,
    rejected_by: null,
    rejection_reason: null,
  };
  ownerStayRequests = ownerStayRequests.map((r) => (r.request_id === requestId ? updated : r));

  // Guard: si por cualquier motivo ya existe un block ligado (p.ej. seed),
  // no duplicamos. Usamos owner_stay_request_id como clave.
  const existingBlock = blocks.find((b) => b.owner_stay_request_id === requestId);
  if (!existingBlock) {
    _createBlock({
      villa_id: updated.villa_id,
      type: "OWNER_STAY",
      start_date: updated.start_date,
      end_date: updated.end_date,
      fairness_compensable: false,
      reason: "Uso propietario",
      created_by: updated.approved_by,
      owner_stay_request_id: requestId,
    });
  }
  notifyOwnerStays();
  return updated;
}

/**
 * Rechazar una solicitud. Guarda motivo y actor. NO crea block. Idempotente.
 */
export function rejectOwnerStayRequest(requestId, reason, actor = DEMO_REPORTER) {
  const cur = ownerStayRequests.find((r) => r.request_id === requestId);
  if (!cur) return null;
  if (cur.status === "rejected") return cur;

  const now = demoNowISO();
  const updated = {
    ...cur,
    status: "rejected",
    rejected_at: now,
    rejected_by: actor.user_id || DEMO_REPORTER.user_id,
    rejection_reason: reason || "Sin motivo especificado",
    approved_at: null,
    approved_by: null,
  };
  ownerStayRequests = ownerStayRequests.map((r) => (r.request_id === requestId ? updated : r));
  notifyOwnerStays();
  return updated;
}
